import { useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, Clock, Users, MapPin, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getTourBySlug } from "../data/experiences";
import { formatPrice, shouldShowCurrencyNote } from "../i18n/pricing";
import { LocalizedLink } from "../lib/i18nRouting";
import SEO from "../components/site/SEO";
import { localizedPath } from "../i18n";
import Reveal from "../components/Reveal";
import { DEFAULT_LANG } from "../i18n";

export default function ExperienceDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  const exp = getTourBySlug(slug);
  if (!exp) return <Navigate to="/experiente" replace />;

  const name = t(`tours.${slug}.name`);
  const short = t(`tours.${slug}.short`);
  const description = t(`tours.${slug}.description`);
  const duration = t(`tours.${slug}.duration`);
  const groupSize = t(`tours.${slug}.groupSize`);
  const price = formatPrice(exp.price, lang, slug);

  const highlights = Array.from({ length: exp.highlightsCount || 0 }, (_, i) =>
    t(`tours.${slug}.highlights.${i + 1}`)
  );
  const includes = exp.includesCount
    ? Array.from({ length: exp.includesCount }, (_, i) => t(`tours.${slug}.includes.${i + 1}`))
    : null;

  const pageUrl = `https://apuseniactive.ro${localizedPath(`/experiente/${slug}`, lang)}`;
  const tourJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristTrip",
        "@id": pageUrl + "#trip",
        name,
        description,
        url: pageUrl,
        image: exp.image,
        inLanguage: lang,
        provider: { "@id": "https://apuseniactive.ro/#organization" },
        touristType: exp.category === "apuseni" ? "Multi-day guided e-bike tour" : "Guided e-bike day tour",
        itinerary: { "@type": "Place", name: exp.category === "apuseni" ? "Apuseni Mountains" : "Oradea", address: { "@type": "PostalAddress", addressCountry: "RO" } },
        offers: { "@type": "Offer", price: exp.price, priceCurrency: "RON", availability: "https://schema.org/InStock", url: pageUrl },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Apuseni Active", item: `https://apuseniactive.ro${localizedPath("/", lang)}` },
          { "@type": "ListItem", position: 2, name: t("nav.experiences"), item: `https://apuseniactive.ro${localizedPath("/experiente", lang)}` },
          { "@type": "ListItem", position: 3, name, item: pageUrl },
        ],
      },
    ],
  };

  return (
    <div className="bg-bone min-h-screen" data-testid="experience-detail-page">
      <SEO
        title={`${name} | Apuseni Active`}
        description={short}
        path={`/experiente/${slug}`}
        image={exp.image}
        extraJsonLd={tourJsonLd}
      />

      <section className="relative pt-32 lg:pt-40 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <LocalizedLink to="/experiente" className="text-xs uppercase tracking-wider-3 text-moss link-underline">
              {t("experience_detail.back")}
            </LocalizedLink>
            <div className="mt-8 text-xs uppercase tracking-wider-3 text-terracotta">
              {exp.category === "oradea" ? "Oradea" : "Apuseni"}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-forest leading-[1.02] mt-4 max-w-5xl">{name}</h1>
            <p className="mt-8 max-w-2xl text-muted-foreground text-base md:text-lg leading-relaxed">{short}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="aspect-[16/9] overflow-hidden bg-stone-warm">
            <img src={exp.image} alt={name} className="w-full h-full object-cover" />
          </Reveal>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <Reveal>
              <div className="text-xs uppercase tracking-wider-3 text-moss mb-6">{t("experience_detail.about")}</div>
              <p className="text-base md:text-lg text-forest leading-relaxed">{description}</p>
            </Reveal>

            {exp.secondaryImage && (
              <Reveal className="mt-12">
                <div className="overflow-hidden bg-forest-deep">
                  <img src={exp.secondaryImage} alt={name} loading="lazy" decoding="async" className="w-full h-auto block max-h-[80vh] object-contain mx-auto" />
                </div>
              </Reveal>
            )}

            <Reveal className="mt-12">
              <div className="text-xs uppercase tracking-wider-3 text-moss mb-6">{t("experience_detail.what_you_live")}</div>
              <ul className="space-y-4">
                {highlights.map((h, idx) => (
                  <li key={idx} className="flex items-start gap-4 border-b border-bone-deep pb-4 last:border-0">
                    <Check className="w-5 h-5 text-terracotta stroke-[1.5] mt-0.5 shrink-0" />
                    <span className="text-base text-forest">{h}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            {includes && (
              <Reveal className="mt-12">
                <div className="text-xs uppercase tracking-wider-3 text-moss mb-6">{t("experience_detail.included")}</div>
                <div className="flex flex-wrap gap-2">
                  {includes.map((inc, idx) => (
                    <span key={idx} className="border border-forest text-forest px-4 py-2 text-xs uppercase tracking-wider-2">{inc}</span>
                  ))}
                </div>
              </Reveal>
            )}

            {exp.hasNonRiderNote && (
              <Reveal className="mt-12">
                <div className="text-xs uppercase tracking-wider-3 text-terracotta mb-4">{t("experience_detail.non_rider_partners")}</div>
                <p className="text-base md:text-lg text-forest leading-relaxed border-l-2 border-terracotta pl-6">
                  {t(`tours.${slug}.non_rider_note`)}
                </p>
              </Reveal>
            )}

            {exp.localExperiences && exp.localExperiences.length > 0 && (
              <Reveal className="mt-16">
                <div className="text-xs uppercase tracking-wider-3 text-terracotta mb-2">{t("experience_detail.local_experiences")}</div>
                <h3 className="font-display text-3xl md:text-4xl text-forest leading-tight mb-8">
                  {t("experience_detail.local_experiences_title")}
                </h3>
                <div className="space-y-8">
                  {exp.localExperiences.map((le, idx) => {
                    const lPrice = le.priceOnRequest ? null : formatPrice(le.priceRON, lang, le.priceKey);
                    const localT = (subKey) => t(`tours.${le.key}.${subKey}`);
                    return (
                      <div key={le.key} className="bg-forest text-bone relative overflow-hidden">
                        <div className="absolute inset-0 grain pointer-events-none" />
                        <div className="absolute -right-4 -top-8 font-display text-[14rem] leading-none text-bone/[0.04] pointer-events-none select-none">
                          0{idx + 1}
                        </div>
                        <div className="relative p-8 lg:p-12">
                          <div className="flex items-start justify-between gap-6 flex-wrap">
                            <div className="max-w-xl">
                              <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider-3 text-terracotta">
                                <span className="inline-block w-6 h-px bg-terracotta" />
                                {localT("subtitle")}
                              </div>
                              <h4 className="font-display text-3xl md:text-4xl lg:text-5xl mt-4 leading-[1.05]">
                                {localT("title")}
                              </h4>
                            </div>
                            <div className="text-right shrink-0 border-l border-bone/20 pl-6">
                              <div className="text-[10px] uppercase tracking-wider-3 opacity-70">{t("experience_detail.supplement")}</div>
                              {le.priceOnRequest ? (
                                <>
                                  <div className="font-display text-3xl md:text-4xl mt-1 italic">{t("experience_detail.on_request")}</div>
                                  <div className="text-[10px] uppercase tracking-wider-2 opacity-70">{t("experience_detail.custom_price")}</div>
                                </>
                              ) : (
                                <>
                                  <div className="font-display text-4xl md:text-5xl mt-1">+{lPrice}</div>
                                  <div className="text-[10px] uppercase tracking-wider-2 opacity-70">{t("common.per_person_full")}</div>
                                </>
                              )}
                            </div>
                          </div>
                          <p className="mt-8 text-bone/85 text-base md:text-lg leading-relaxed max-w-3xl font-display italic">
                            „{localT(le.descField)}&rdquo;
                          </p>
                          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-bone/10 border border-bone/15">
                            {Array.from({ length: le.pointsCount }, (_, i) => (
                              <li key={i} className="bg-forest p-5">
                                <div className="text-terracotta text-[10px] uppercase tracking-wider-3 mb-3">0{i+1}</div>
                                <div className="text-sm leading-relaxed">{localT(`points.${i+1}`)}</div>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-8 text-[10px] uppercase tracking-wider-3 text-bone/60">
                            {t("experience_detail.opt_in_note")}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Reveal>
            )}
          </div>

          <div className="md:col-span-5">
            <Reveal className="md:sticky md:top-28">
              <div className="bg-forest text-bone p-8 lg:p-10">
                <div className="text-xs uppercase tracking-wider-3 opacity-70">{t("experience_detail.book_from")}</div>
                <div className="font-display text-6xl mt-2">{price}</div>
                <div className="text-xs uppercase tracking-wider-2 opacity-70 mt-2">{t("common.per_person_full")}</div>
                {exp.hasPricingNote && (
                  <div className="text-xs opacity-75 mt-3 leading-relaxed">{t(`tours.${slug}.pricing_note`)}</div>
                )}

                <div className="mt-10 space-y-4 text-sm">
                  <Row icon={<Clock className="w-4 h-4" />} label={t("common.duration")} value={duration} />
                  <Row icon={<Users className="w-4 h-4" />} label={t("common.group")} value={groupSize} />
                  <Row icon={<MapPin className="w-4 h-4" />} label={t("common.starting_from")} value={t("experience_detail.starts_from")} />
                </div>

                <LocalizedLink
                  to={`/rezervare?experienta=${slug}`}
                  data-testid="detail-book-btn"
                  className="btn-lift mt-10 w-full inline-flex items-center justify-center gap-3 bg-bone text-forest px-8 py-4 text-xs uppercase tracking-wider-3 hover:bg-bone-deep"
                >
                  {t("common.book_now")} <ArrowUpRight className="w-4 h-4" />
                </LocalizedLink>

                <p className="mt-6 text-xs opacity-70 leading-relaxed">{t("experience_detail.book_note")}</p>
                {shouldShowCurrencyNote(lang) && (
                  <div className="mt-4 text-[10px] uppercase tracking-wider-3 opacity-60">
                    {t("currency_note_short")}
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-bone/15 pb-3">
      <div className="flex items-center gap-3 opacity-80">{icon}<span className="text-xs uppercase tracking-wider-2">{label}</span></div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
